import { Box, IconButton, inputBaseClasses, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { CircleUser, SendHorizontal, X } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { useRootStore } from '~/base/hooks/useRootStore';
import { OutlinedInputWithController } from '~/components/reuseable/OutlinedInputWithController';
import { QSvgIcon } from '~/components/reuseable/QSvgIcon';
import DateHelper from '~/helpers/DateHelper';
import { useMFMessagesContext } from '~/microfrontend/hooks/useMFModuleNameContext';
import { MessageForm, MessageFormField } from '~/modules/messages/forms/MessageForm';
import MessagesHelper from '~/modules/messages/helpers/MessagesHelper';
import MessagesListItemModel from '~/modules/messages/models/MessagesListItemModel';
import { MessageSenderEnum } from '~/modules/messages/types/MessageSenderEnum';
import dealerIcon from '~assets/images/icons/q_dealer.svg';
import distributorIcon from '~assets/images/icons/q_distributor.svg';

export const MessagesWidget = observer(() => {
  const { messagesStore } = useRootStore();

  const { documentId, documentTypeId, isDisabled } = useMFMessagesContext();

  const { t } = useTranslation(['messages', 'common']);

  const { classes, cx } = useStyles();

  const { palette } = useTheme();

  const messageForm = useForm<MessageForm>({
    defaultValues: { ...MessageForm.create() },
  });

  const { handleSubmit, reset } = messageForm;

  //Effects

  useEffect(() => {
    messagesStore.getList(documentTypeId, documentId);
  }, [documentId, documentTypeId]);

  // Handlers

  const handleCreateMessage = (values: MessageForm) => {
    if (!documentId || !documentTypeId) {
      return;
    }

    messagesStore.createMessage(documentTypeId, documentId, values);
    reset({ ...MessageForm.create() });
  };

  const handleDeleteMessage = (messageId: number | null) => {
    if (!documentId || !documentTypeId || !messageId) {
      return;
    }

    messagesStore.deleteMessage(documentTypeId, documentId, messageId);
  };

  // Renders

  const renderMessageSenderIcon = (senderType: MessageSenderEnum | null) => {
    if (!senderType) {
      return;
    }

    switch (senderType) {
      case MessageSenderEnum.DEALER:
        return <QSvgIcon src={dealerIcon} color={palette.primary.main} />;

      case MessageSenderEnum.DISTRIBUTOR:
        return <QSvgIcon src={distributorIcon} color={palette.primary.main} />;

      default:
        return <CircleUser color={palette.primary.main} />;
    }
  };

  const renderMessage = (message: MessagesListItemModel) => (
    <Stack key={message.descr} direction="column">
      <Stack direction="row" spacing={2} alignItems="center">
        <Tooltip
          title={message.senderType !== MessageSenderEnum.CLIENT && t(`tooltip.messageSender.${message.senderType!}`)}
        >
          <Box>{renderMessageSenderIcon(message.senderType)}</Box>
        </Tooltip>
        <Typography variant="subtitle2" className={classes.userName}>
          {message.senderFio}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {DateHelper.format(message.dateSend, 'dd.MM.yyyy')}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {DateHelper.format(message.dateSend, 'HH:mm:ss')}
        </Typography>
        <IconButton
          className={cx(classes.deleteButton, { visible: message.isCanDelete })}
          onClick={() => handleDeleteMessage(message.id)}
        >
          <X color={palette.custom.icon.gray} />
        </IconButton>
      </Stack>
      <Typography whiteSpace="pre-wrap" variant="body1" className={classes.messageText}>
        {MessagesHelper.convertHtmlBrToNewlines(message.descr)}
      </Typography>
    </Stack>
  );

  return (
    <>
      <Stack direction="column" spacing={1} mb={2}>
        {messagesStore.list.map(renderMessage)}
      </Stack>

      <FormProvider {...messageForm}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <OutlinedInputWithController
            name={MessageFormField.message}
            className={classes.resizableField}
            fullWidth
            multiline
            size="small"
            label={t('newMessage.label')}
            disabled={isDisabled}
          />
          <IconButton color="primary" type="button" onClick={handleSubmit(handleCreateMessage)} disabled={isDisabled}>
            <SendHorizontal />
          </IconButton>
        </Stack>
      </FormProvider>
    </>
  );
});

const useStyles = makeStyles()((theme) => ({
  userName: {
    flexGrow: 1,
  },
  deleteButton: {
    visibility: 'hidden',
    '&.visible': {
      visibility: 'visible',
    },
  },
  messageText: {
    margin: theme.spacing(0, 25, 0, 5),
    whiteSpace: 'pre-line',
  },
  resizableField: {
    [`& .${inputBaseClasses.root}`]: {
      padding: '5px 0 0',
      '& textarea': {
        overflow: 'auto !important',
        padding: theme.spacing(1, 2),
        minHeight: 20,
        maxHeight: 184,
        '::-webkit-scrollbar': {
          width: 15,
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.grey[400],
          backgroundClip: 'padding-box',
          border: '4px solid transparent',
          borderRadius: 7,
        },
      },
    },
  },
}));
